import * as z from "zod/v4";

export const fraudCategories = [
  "serial_return",
  "price_manipulation",
  "timing_exploitation",
  "wardrobing",
  "ftid",
  "customer_history",
] as const;
export type FraudCategory = (typeof fraudCategories)[number];

const FraudCategoryOutput_ = z.object({
  category: z
    .enum(fraudCategories)
    .describe("The fraud category this assessment is for."),
  confidence: z
    .number()
    .min(0)
    .max(1)
    .describe(
      "Confidence score from 0 to 1. 0 means no fraud risk, 1 means certain fraud.",
    ),
  detected: z
    .boolean()
    .describe(
      "Whether fraud signals were detected for this category. True if confidence >= 0.5.",
    ),
  explanation: z
    .string()
    .describe(
      "Brief explanation of why this confidence score was assigned, citing specific data points.",
    ),
  signals: z
    .array(z.string())
    .describe(
      "List of specific fraud signals detected, e.g. 'high return velocity', 'vague reasons'.",
    ),
  summary: z
    .string()
    .describe(
      "A concise, human-readable summary of your assessment — what's going on and why you reached this conclusion.",
    ),
  evidence: z
    .array(z.string())
    .describe(
      "Specific evidence items that support your assessment. Each item should cite a concrete data point or pattern you observed.",
    ),
});
export const FraudCategoryOutput: z.ZodType<FraudCategoryOutput> =
  FraudCategoryOutput_;
export interface FraudCategoryOutput
  extends z.infer<typeof FraudCategoryOutput_> {}

const FraudAssessment_ = z.object({
  overallFraudScore: z
    .number()
    .min(0)
    .max(1)
    .describe("Overall fraud risk score from 0 to 1."),
  categories: z
    .array(FraudCategoryOutput)
    .describe("Individual fraud category assessments."),
  summary: z
    .string()
    .describe("One-sentence summary of the overall fraud assessment."),
  primaryCategory: z
    .enum(fraudCategories)
    .nullable()
    .optional()
    .describe(
      "The most prominent fraud category — the single biggest concern. Absent if no fraud detected.",
    ),
  riskDescription: z
    .string()
    .describe(
      "One concise sentence describing the risk and which category is the most prominent problem.",
    ),
});
export const FraudAssessment: z.ZodType<FraudAssessment> = FraudAssessment_;
export interface FraudAssessment extends z.infer<typeof FraudAssessment_> {}
